/*****************************************************
Naam: Dashboard.jsx
Locatie: src/components/Dashboard/Dashboard.jsx
Datum: 2026-02-25
Versie: 2.0.0
Auteur: Ragnar Standhardt
******************************************************
Beschrijving:
Hoofd dashboard component - ORCHESTRATOR.
Gebruikt hooks voor state management en sub-componenten voor UI.

Structuur:
  hooks/useDashboardCounts.js    → Tellers (aanvragen, DOT, deadlines)
  hooks/useDashboardSettings.js  → Kleuren, logo's
  hooks/useSocketConnection.js   → Socket.IO real-time events
  hooks/useEmailQueue.js         → Email queue management
  DashboardHeader.jsx            → Logo + achtergrond
  DashboardClock.jsx             → Klok
  DashboardTopButtons.jsx        → Status, Email knoppen
  DashboardMainButtons.jsx       → Linker + rechter kolom knoppen
  DashboardSideButtons.jsx       → Notifications, Settings, Logout
  DashboardModals.jsx            → Alle modals (lazy loaded)
  utils/dashboardHelpers.js      → Helper functies

CHANGELOG v2.0.0 (2026-02-25):
- Refactored: Monoliet opgesplitst in hooks en sub-componenten
- NIEUW: deadlineVerlopenCount via useDashboardCounts
- NIEUW: deadline-verlopen socket listener via useSocketConnection
- NIEUW: Rode Prijsopgave knop bij verlopen deadlines
******************************************************/

import React, { useState, useEffect, useCallback } from 'react';

// Hooks
import { useDashboardCounts } from './hooks/useDashboardCounts';
import { useDashboardSettings } from './hooks/useDashboardSettings';
import { useSocketConnection } from './hooks/useSocketConnection';
import { useEmailQueue } from './hooks/useEmailQueue';

// Sub-componenten
import DashboardHeader from './DashboardHeader';
import DashboardClock from './DashboardClock';
import DashboardTopButtons from './DashboardTopButtons';
import DashboardMainButtons from './DashboardMainButtons';
import DashboardSideButtons from './DashboardSideButtons';
import DashboardModals from './DashboardModals';
import GeofenceNotification from './GeofenceNotification';

// ============================================
// DASHBOARD COMPONENT
// ============================================

const Dashboard = ({ onLogout }) => {
  // ---- Modal & UI state ----
  const [activeModal, setActiveModal] = useState(null);
  const [selectedAanvraagID, setSelectedAanvraagID] = useState(null);
  // WhatsApp verwijderd
  const [showTracking, setShowTracking] = useState(false);
  const [showBerichten, setShowBerichten] = useState(false);
  const [berichtenCount, setBerichtenCount] = useState(0);

  // ---- GEOfencing notificaties ----
  const [geofenceEvents, setGeofenceEvents] = useState([]);
  const handleGeofenceEvent = useCallback((event) => {
    setGeofenceEvents(prev => {
      const withId = { ...event, id: `${Date.now()}-${Math.random()}` };
      return [...prev.slice(-4), withId];
    });
  }, []);
  const dismissGeofenceEvent = useCallback((id) => {
    setGeofenceEvents(prev => prev.filter(e => e.id !== id));
  }, []);

  // ---- Hooks ----
  const {
    companyLogo, backgroundLogo, dashboardSettings,
    handleLogoUpdate, handleBackgroundUpdate
  } = useDashboardSettings();

  const {
    nieuweAanvragenCount, totaalAanvragenCount, prijzenBinnenCount, opdrachtBevestigingCount,
    notificationCount, dotVerlopenCount, trackingOverdueCount,
    deadlineVerlopenCount, afgeschrevenCount, geoffreerdCount, klantenCount, vervoerdersCount,
    factuurVervallenCount,
    refreshAllCounts, loadNotificationCount,
    loadDeadlineVerlopenCount
  } = useDashboardCounts();

  const {
    emailQueue, emailData, showPreview, previewEmail,
    loadEmailQueue, setShowPreview, clearEmailData,
    handleCreateAanvraagFromQueue: emailQueueCreateHandler,
    handleDeleteEmailFromQueue: emailQueueDeleteHandler,
    handlePreviewEmail,
    handleImportFromPreview: emailQueueImportHandler,
    handleWhatsAppImport: emailQueueWhatsAppHandler
  } = useEmailQueue();

  // ---- Berichten count laden (moet voor useSocketConnection staan) ----
  const loadBerichtenCount = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/all-customer-messages');
      const data = await res.json();
      if (data.success) setBerichtenCount(data.totaalOngelezen || 0);
    } catch(e) {}
  }, []);

  // Socket met callbacks voor count refresh + deadline update + berichten + geofencing
  useSocketConnection(loadEmailQueue, refreshAllCounts, loadDeadlineVerlopenCount, loadBerichtenCount, handleGeofenceEvent);

  // ---- Button styling ----
  const getButtonStyle = useCallback((buttonKey) => {
    // Vervoerders knop rood bij verlopen DOT
    if (buttonKey === 'vervoerders' && dotVerlopenCount > 0) {
      return {
        backgroundColor: '#dc2626',
        color: '#ffffff',
        boxShadow: '0 8px 20px rgba(220,38,38,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
      };
    }
    // Pallet Tracking knop rood bij te late aanvragen
    if (buttonKey === 'palletTracking' && trackingOverdueCount > 0) {
      return {
        backgroundColor: '#dc2626',
        color: '#ffffff',
        boxShadow: '0 8px 20px rgba(220,38,38,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
      };
    }
    const bgColor = dashboardSettings.buttonColors?.[buttonKey] || '#10b981';
    const textColor = dashboardSettings.buttonTextColors?.[buttonKey] || '#ffffff';
    return {
      backgroundColor: bgColor,
      color: textColor,
      boxShadow: '0 8px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)'
    };
  }, [dashboardSettings, dotVerlopenCount, trackingOverdueCount]);

  // ---- Load email queue on mount ----
  useEffect(() => {
    loadEmailQueue();
  }, [loadEmailQueue]);

  useEffect(() => {
    loadBerichtenCount();
    const interval = setInterval(loadBerichtenCount, 30000);
    return () => clearInterval(interval);
  }, [loadBerichtenCount]);

  // ---- Modal handlers ----
  const handleModalClose = useCallback(() => {
    setActiveModal(null);
  }, []);

  const handleModalCloseWithCleanup = useCallback(() => {
    setActiveModal(null);
    setSelectedAanvraagID(null);
    clearEmailData();
    // Direct counts refreshen wanneer modal sluit
    refreshAllCounts();
  }, [clearEmailData, refreshAllCounts]);

  // Functie om modal te openen met specifieke aanvraag (gebruikt door StatusAanvragenModal)
  const handleModalOpen = useCallback((modalType, aanvraagID = null) => {
    setSelectedAanvraagID(aanvraagID);
    setActiveModal(modalType);
  }, []);

  // ---- Email queue wrappers ----
  // Deze wrappen de useEmailQueue handlers om ook activeModal te zetten

  const handleCreateAanvraagFromQueue = useCallback(async (email) => {
    const modalType = await emailQueueCreateHandler(email);
    setActiveModal(modalType);
  }, [emailQueueCreateHandler]);

  const handleDeleteEmailFromQueue = useCallback(async (emailId) => {
    await emailQueueDeleteHandler(emailId);
  }, [emailQueueDeleteHandler]);

  const handleImportFromPreview = useCallback(async (emailDataInput) => {
    const modalType = await emailQueueImportHandler(emailDataInput);
    setActiveModal(modalType);
  }, [emailQueueImportHandler]);

  const handleWhatsAppImport = useCallback((parsedData) => {
    const modalType = emailQueueWhatsAppHandler(parsedData);
    setActiveModal(modalType);
  }, [emailQueueWhatsAppHandler]);

  // ---- RENDER ----
  return (
    <div 
      className="h-screen relative flex flex-col"
      style={{ backgroundColor: dashboardSettings.backgroundColor || '#10b981' }}
    >
      {/* Achtergrond + Logo */}
      <DashboardHeader 
        companyLogo={companyLogo} 
        backgroundLogo={backgroundLogo} 
      />

      {/* Top buttons: Status, Email Queue */}
      <DashboardTopButtons
        emailQueueLength={emailQueue.length}
        totaalAanvragenCount={totaalAanvragenCount}
        getButtonStyle={getButtonStyle}
        onOpenStatus={() => setActiveModal('status')}
        onOpenEmailQueue={() => setActiveModal('emailQueue')}
        onOpenWhatsApp={null}
      />

      {/* Main content area: linker + rechter kolom */}
      <DashboardMainButtons
        getButtonStyle={getButtonStyle}
        totaalAanvragenCount={totaalAanvragenCount}
        nieuweAanvragenCount={nieuweAanvragenCount}
        prijzenBinnenCount={prijzenBinnenCount}
        opdrachtBevestigingCount={opdrachtBevestigingCount}
        dotVerlopenCount={dotVerlopenCount}
        trackingOverdueCount={trackingOverdueCount}
        deadlineVerlopenCount={deadlineVerlopenCount}
        afgeschrevenCount={afgeschrevenCount}
        klantenCount={klantenCount}
        vervoerdersCount={vervoerdersCount}
        geoffreerdCount={geoffreerdCount}
        factuurVervallenCount={factuurVervallenCount}
        onOpenAanvraag={() => setActiveModal('aanvraag')}
        onOpenPrijsopgave={() => setActiveModal('prijsopgave')}
        onOpenGeoffreerd={() => setActiveModal('geoffreerd')}
        onOpenOpdrachtbevestiging={() => setActiveModal('opdrachtbevestiging')}
        onOpenTracking={() => setShowTracking(true)}
        onOpenKlanten={() => setActiveModal('klanten')}
        onOpenVervoerders={() => setActiveModal('vervoerders')}
        onOpenLijsten={() => setActiveModal('lijsten')}
        onOpenFacturen={() => setActiveModal('facturen')}
        onOpenAfgeschreven={() => setActiveModal('afgeschreven')}
      />

      {/* Bottom section */}
      <div className="pb-8 relative">
        {/* Klok in center bottom */}
        <div className="text-center mb-4">
          <DashboardClock />
        </div>

        {/* Side buttons: Notifications, Email Check, Settings, Logout */}
        <DashboardSideButtons
          getButtonStyle={getButtonStyle}
          notificationCount={notificationCount}
          berichtenCount={berichtenCount}
          onOpenNotifications={() => setActiveModal('notifications')}
          onOpenEmailCheck={() => setActiveModal('emailCheck')}
          onOpenSettings={() => setActiveModal('instellingen')}
          onLogout={onLogout}
          onOpenBerichten={() => { setShowBerichten(true); setBerichtenCount(0); }}
        />
      </div>

      {/* Alle modals */}
      <DashboardModals
        activeModal={activeModal}
        selectedAanvraagID={selectedAanvraagID}
        emailData={emailData}
        emailQueue={emailQueue}
        showPreview={showPreview}
        previewEmail={previewEmail}
        showWhatsAppImport={false}
        showTracking={showTracking}
        onModalClose={handleModalClose}
        onModalCloseWithCleanup={handleModalCloseWithCleanup}
        onModalOpen={handleModalOpen}
        onLogoUpdate={handleLogoUpdate}
        onBackgroundUpdate={handleBackgroundUpdate}
        onLoadNotificationCount={loadNotificationCount}
        refreshDashboardCounts={refreshAllCounts}
        onCreateAanvraagFromQueue={handleCreateAanvraagFromQueue}
        onDeleteEmailFromQueue={handleDeleteEmailFromQueue}
        onPreviewEmail={handlePreviewEmail}
        onImportFromPreview={handleImportFromPreview}
        onClosePreview={() => setShowPreview(false)}
        onWhatsAppImport={null}
        onCloseWhatsApp={() => {}}
        onCloseTracking={() => setShowTracking(false)}
        showBerichten={showBerichten}
        onCloseBerichten={() => { setShowBerichten(false); loadBerichtenCount(); }}
      />

      {/* 📍 GEOfencing hub passage notificaties */}
      <GeofenceNotification
        events={geofenceEvents}
        onDismiss={dismissGeofenceEvent}
      />
    </div>
  );
};

export default Dashboard;
